/**
 * TouchAwareTooltip - A wrapper around Chakra UI Tooltip that automatically
 * disables tooltips on touch devices to prevent unwanted hover states.
 *
 * On touch devices (hover: none and pointer: coarse), this component
 * will not render the Tooltip wrapper, just the children.
 *
 * @example
 * ```tsx
 * <TouchAwareTooltip label="Help text">
 *   <Button>Hover me</Button>
 * </TouchAwareTooltip>
 * ```
 */

"use client";

import { Tooltip, type TooltipProps } from "@chakra-ui/react";
import { useEffect, useState } from "react";

export interface TouchAwareTooltipProps extends TooltipProps {
  children: React.ReactNode;
}

/**
 * Detects if the device supports hover (not a touch device)
 */
function useIsHoverDevice(): boolean {
  const [isHoverDevice, setIsHoverDevice] = useState(true);

  useEffect(() => {
    // Check if device supports hover
    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    
    const updateIsHoverDevice = () => {
      setIsHoverDevice(mediaQuery.matches);
    };

    // Set initial value
    updateIsHoverDevice();

    // Listen for changes (e.g., when device orientation changes)
    mediaQuery.addEventListener("change", updateIsHoverDevice);

    return () => {
      mediaQuery.removeEventListener("change", updateIsHoverDevice);
    };
  }, []);

  return isHoverDevice;
}

export const TouchAwareTooltip = ({
  children,
  ...tooltipProps
}: TouchAwareTooltipProps) => {
  const isHoverDevice = useIsHoverDevice();

  // On touch devices, just render children without tooltip
  if (!isHoverDevice) {
    return <>{children}</>;
  }

  // On hover-capable devices, render with tooltip
  return <Tooltip {...tooltipProps}>{children}</Tooltip>;
};
